import { ArrowRight, AlertTriangle, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { useNavigate } from '@tanstack/react-router'

type MetricTone = 'default' | 'warning' | 'danger' | 'success'

export interface ModuleSummaryMetric {
  label: string
  value: string
  tone?: MetricTone
}

export interface ModuleSummaryCardProps {
  icon: LucideIcon
  title: string
  path: string
  metrics: ModuleSummaryMetric[]
  loading?: boolean
  /**
   * Set when the query behind `metrics` rejected. Callers derive metrics
   * with `data?.x ?? 0`, so on failure every tile would otherwise read a
   * confident "0" — the dashboard would state there are no open orders
   * when it simply never got an answer. Renders "—" plus a failure hint
   * instead.
   */
  error?: boolean
}

const VALUE_TONE: Record<MetricTone, string> = {
  default: 'text-primary',
  warning: 'text-warning-text',
  danger: 'text-danger-text',
  success: 'text-success-text',
}

export function ModuleSummaryCard({
  icon: Icon,
  title,
  path,
  metrics,
  loading,
  error,
}: ModuleSummaryCardProps) {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigate({ to: path })}
      className={cn(
        'group flex min-w-0 cursor-pointer flex-col gap-3 rounded-sm border border-border-subtle bg-surface p-4 text-left transition-[border-color,box-shadow] duration-fast',
        'hover:border-border-default hover:shadow-sm',
      )}
    >
      <header className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-role-section-label">
          <Icon size={12} aria-hidden="true" />
          {title}
        </span>
        {error ? (
          <span
            className="inline-flex items-center gap-1 text-2xs text-danger-text"
            title="This module's data failed to load — open it to retry."
          >
            <AlertTriangle size={12} aria-hidden="true" />
            Unavailable
          </span>
        ) : (
          <ArrowRight
            size={14}
            className="text-tertiary transition-colors duration-fast group-hover:text-primary"
            aria-hidden="true"
          />
        )}
      </header>
      <dl className="flex flex-col gap-1">
        {metrics.map((m) => (
          <div key={m.label} className="flex items-baseline justify-between gap-3">
            <dt className="text-sm text-secondary">{m.label}</dt>
            <dd
              className={cn(
                'text-lg font-semibold tabular-nums',
                VALUE_TONE[m.tone ?? 'default'],
                (loading || error) && 'opacity-40',
              )}
            >
              {loading || error ? '—' : m.value}
            </dd>
          </div>
        ))}
      </dl>
    </button>
  )
}
